import {
  Badge,
  Box,
  HStack,
  Icon,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Tr,
  Thead,
  Stack,
  VStack,
  Center,
  Spinner,
} from '@chakra-ui/react';
import { EnrolmentIssuesItem } from './types';
import upperFirst from 'lodash-es/upperFirst.js';
import { useCallback, useState, useMemo } from 'react';
import { CheckIcon } from 'lucide-react';

function IssueBadge({ status }) {
  const isStatusOpen = status === 'Open';
  const badgeColor = isStatusOpen ? 'danger' : 'success';
  const badgeBg = isStatusOpen ? 'bg.danger-subtle' : 'bg.success-subtle';

  return (
    <Badge
      borderRadius="full"
      borderColor={badgeColor}
      borderWidth="1px"
      bg={badgeBg}
      color={badgeColor}
      padding="2px 10px"
    >
      <Text fontSize="xs" fontWeight="bold">
        {upperFirst(status)}
      </Text>
    </Badge>
  );
}

function IssueRow({
  issue,
  icon,
  label,
  status,
  canResolve = true,
  isVerifying,
  handleClick,
}: {
  issue: string;
  icon: React.ElementType;
  label: string;
  status?: string;
  canResolve?: boolean;
  isVerifying: boolean;
  handleClick: (issue: string) => void;
}) {
  return (
    <Tr key={issue}>
      <Td>
        <HStack>
          <Box as="span">
            {status === 'Resolved' ? (
              <Icon as={CheckIcon} color={'success '} boxSize="5" />
            ) : (
              <Icon as={icon} color={'danger'} boxSize="5" />
            )}
          </Box>
          <Text>{label}</Text>
        </HStack>
      </Td>
      <Td>
        {status && (
          <Stack align={'center'} direction={'row'} spacing={1}>
            <IssueBadge status={status} />
          </Stack>
        )}
      </Td>
      <Td textAlign="right">
        {status === 'Open' && canResolve ? (
          <ResolveButton isVerifying={isVerifying} handleClick={handleClick} issue={issue} />
        ) : null}
      </Td>
    </Tr>
  );
}

function ResolveButton({
  isVerifying,
  handleClick,
  issue,
}: {
  isVerifying: boolean;
  handleClick: (issue: string) => void;
  issue: string;
}) {
  return (
    <Box
      as="button"
      borderColor="border.emphasis"
      borderWidth="1px"
      borderRadius="lg"
      bg="bg.surface"
      color="fg.emphasis"
      padding="8px 10px"
      onClick={() => handleClick(issue)}
      _hover={{
        bg: 'bg.muted',
      }}
    >
      <HStack>
        {isVerifying ? (
          <Box as={Spinner} size="20px" padding={1} mr={1} />
        ) : (
          <Box
            as={CheckIcon}
            size="20px"
            borderRadius="full"
            bg="success"
            color="white"
            padding={1}
            mr={2}
          />
        )}
        <Text fontWeight="bold" fontSize="md">
          {isVerifying ? 'Verifying...' : 'Resolve'}
        </Text>
      </HStack>
    </Box>
  );
}

export function EnrolmentIssues({
  issues,
  onIssue,
  isLoading = false,
}: {
  issues: EnrolmentIssuesItem;
  isLoading?: boolean;
  onIssue: (issue: string) => void;
}) {
  const [isVerifying, setIsVerifying] = useState(isLoading);

  const handleClick = useCallback(
    (issue: string) => {
      onIssue(issue);
    },
    [onIssue],
  );

  useMemo(() => {
    if (isVerifying !== isLoading) {
      setIsVerifying(isLoading);
    }
  }, [isLoading, isVerifying]);

  return (
    <Box bg="bg.surface" width="full" overflow="auto">
      <Stack bg="bg.surface" gap="0">
        <Box overflowX="auto" width="full">
          {Object.entries(issues).length > 0 ? (
            <Table>
              <Thead>
                <Tr>
                  <Th>Issues</Th>
                  <Th>Status</Th>
                  <Th></Th>
                </Tr>
              </Thead>
              <Tbody>
                {Object.entries(issues).map(([key, { label, icon, status, canResolve }]) => (
                  <IssueRow
                    key={key}
                    issue={key}
                    icon={icon}
                    label={label}
                    status={status}
                    canResolve={canResolve}
                    isVerifying={isVerifying}
                    handleClick={handleClick}
                  />
                ))}
              </Tbody>
            </Table>
          ) : (
            <Center>
              <VStack spacing="2">
                <Box
                  borderColor="border.emphasis"
                  borderWidth="1px"
                  borderRadius="lg"
                  bg="bg.surface"
                  color="fg.emphasis"
                  padding="8px 10px"
                  m={5}
                >
                  <HStack>
                    <Box
                      as={CheckIcon}
                      size="20px"
                      borderRadius="full"
                      bg="success"
                      color="white"
                      padding={1}
                      mr={2}
                    />
                    <Text fontWeight="bold" fontSize="md">
                      Course enrolment has no issues
                    </Text>
                  </HStack>
                </Box>
              </VStack>
            </Center>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
